import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Avatar,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Badge,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  getUserProfile,
  updateUserProfile,
  logout,
  onAuthChange,
  getCurrentUser,
  getPartnerRequests,
  updatePartnerRequestStatus,
  deletePartnerRequest,
} from '../lib/api-client';
import { useMatchNotification } from '../context/MatchNotificationContext';
import './profile.scss';
import type { UserProfile, PartnerRequest } from '../types';
import { defaultDivisions } from '../constants';

const ProfileContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const ProfileCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  marginBottom: theme.spacing(3),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  marginBottom: theme.spacing(2),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontSize: '2.5rem',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: '#667eea',
    },
  },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderColor: '#667eea',
  },
}));

const EditButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  background: '#2c2c2c',
  color: '#f5f5f5',
  textTransform: 'none',
  minWidth: '60px',
  '&:hover': {
    background: '#1a1a1a',
  },
}));

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clearMatchBadge } = useMatchNotification();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<UserProfile>>({});
  const [saving, setSaving] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [newDivision, setNewDivision] = useState('');
  const [newRole, setNewRole] = useState('');

  // Map view param to tab index
  const viewToTab: Record<string, number> = { profile: 0, events: 1, requests: 2 };
  const tabToView: Record<number, string> = { 0: 'profile', 1: 'events', 2: 'requests' };
  const initialView = searchParams.get('view') || 'profile';
  const [activeTab, setActiveTab] = useState(viewToTab[initialView] ?? 0);

  // Update URL when tab changes
  const handleTabChange = (tab: number) => {
    setActiveTab(tab);
    setSearchParams({ view: tabToView[tab] });
  };

  // Requests state
  const [incomingRequests, setIncomingRequests] = useState<PartnerRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<PartnerRequest[]>([]);
  const [matchedRequests, setMatchedRequests] = useState<PartnerRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsSubTab, setRequestsSubTab] = useState(0);
  const [expandedRequestIds, setExpandedRequestIds] = useState<Set<string>>(new Set());

  const toggleRequestExpansion = (requestId: string) => {
    setExpandedRequestIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const loadRequests = async (uid: string) => {
    setRequestsLoading(true);
    try {
      const [incoming, outgoing, matched] = await Promise.all([
        getPartnerRequests(uid, 'incoming', 'pending'),
        getPartnerRequests(uid, 'outgoing', 'pending'),
        getPartnerRequests(uid, 'all', 'accepted'),
      ]);
      setIncomingRequests(incoming);
      setOutgoingRequests(outgoing);
      setMatchedRequests(matched);
    } catch (err: any) {
      console.error('Error loading requests:', err);
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const unsubscribe = onAuthChange(async (user) => {
      if (!user) {
        navigate('/sign-in');
        return;
      }

      try {
        const userProfile = await getUserProfile(user.uid);
        if (userProfile) {
          setProfile(userProfile);
          setEditForm({
            displayName: userProfile.displayName,
            profilePicture: userProfile.profilePicture || '',
            wsdcId: userProfile.wsdcId || '',
            location: userProfile.location || '',
            divisions: userProfile.divisions || [],
          });
        }
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load requests when Requests tab is selected
  useEffect(() => {
    if (activeTab === 2 && profile?.uid) {
      loadRequests(profile.uid);
      // Clear the match badge when viewing requests
      clearMatchBadge();
    }
  }, [activeTab, profile?.uid, clearMatchBadge]);

  const handleAcceptRequest = async (requestId: string) => {
    if (!profile) return;
    try {
      await updatePartnerRequestStatus(requestId, 'accepted');
      loadRequests(profile.uid);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeclineRequest = async (requestId: string) => {
    try {
      await updatePartnerRequestStatus(requestId, 'declined');
      setIncomingRequests(incomingRequests.filter((r) => r.id !== requestId));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      await deletePartnerRequest(requestId);
      setOutgoingRequests(outgoingRequests.filter((r) => r.id !== requestId));
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditToggle = () => {
    if (!isEditing) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
      setEditForm({
        displayName: profile?.displayName,
        profilePicture: profile?.profilePicture || '',
        wsdcId: profile?.wsdcId || '',
        location: profile?.location || '',
        divisions: profile?.divisions || [],
      });
      setNewDivision('');
      setNewRole('');
    }
  };

  const handleAddDivision = () => {
    if (newDivision && newRole) {
      const updatedDivisions = [
        ...(editForm.divisions || []),
        { division: newDivision as any, role: newRole.toLowerCase() as 'leader' | 'follower' },
      ];
      setEditForm({ ...editForm, divisions: updatedDivisions });
      setNewDivision('');
      setNewRole('');
    }
  };

  const handleDeleteDivision = (index: number) => {
    const updatedDivisions = [...(editForm.divisions || [])];
    updatedDivisions.splice(index, 1);
    setEditForm({ ...editForm, divisions: updatedDivisions });
  };

  const handleSaveProfile = async () => {
    if (!profile) return;

    setSaving(true);
    setError('');

    try {
      await updateUserProfile(profile.uid, editForm);
      setProfile({ ...profile, ...editForm });
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/sign-in');
    } catch (err: any) {
      console.error('Logout error:', err);
      setError('Failed to log out');
    }
  };

  if (loading) {
    return (
      <ProfileContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </ProfileContainer>
    );
  }

  if (!profile) {
    return (
      <ProfileContainer>
        <Alert severity="error">Profile not found</Alert>
      </ProfileContainer>
    );
  }

  const userInitial = profile.email?.charAt(0).toUpperCase() || '?';

  return (
    <ProfileContainer maxWidth="md">
      {/* Header with Logout */}
      <HeaderBox>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            My Profile
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your account settings and preferences
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={() => setLogoutDialogOpen(true)}
          sx={{
            borderColor: '#e74c3c',
            color: '#e74c3c',
            '&:hover': {
              background: 'rgba(231, 76, 60, 0.1)',
              borderColor: '#e74c3c',
            },
          }}
        >
          Logout
        </Button>
      </HeaderBox>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <ProfileCard>
        {/* Tabs Navigation */}
        <Box
          sx={{ mb: 3, display: 'flex', gap: 1, flexDirection: 'row', justifyContent: 'center' }}
        >
          <Button
            variant={activeTab === 0 ? 'contained' : 'outlined'}
            onClick={() => handleTabChange(0)}
            endIcon={activeTab === 0 ? <CheckIcon /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: activeTab === 0 ? '#2c2c2c' : 'transparent',
              color: activeTab === 0 ? '#fff' : '#2c2c2c',
              borderColor: '#e0e0e0',
              '&:hover': {
                backgroundColor: activeTab === 0 ? '#1a1a1a' : '#f5f5f5',
                borderColor: '#e0e0e0',
              },
            }}
          >
            Profile
          </Button>
          <Button
            variant={activeTab === 1 ? 'contained' : 'outlined'}
            onClick={() => handleTabChange(1)}
            endIcon={activeTab === 1 ? <CheckIcon /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: activeTab === 1 ? '#2c2c2c' : 'transparent',
              color: activeTab === 1 ? '#fff' : '#2c2c2c',
              borderColor: '#e0e0e0',
              '&:hover': {
                backgroundColor: activeTab === 1 ? '#1a1a1a' : '#f5f5f5',
                borderColor: '#e0e0e0',
              },
            }}
          >
            Events
          </Button>
          <Button
            variant={activeTab === 2 ? 'contained' : 'outlined'}
            onClick={() => handleTabChange(2)}
            endIcon={activeTab === 2 ? <CheckIcon /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: activeTab === 2 ? '#2c2c2c' : 'transparent',
              color: activeTab === 2 ? '#fff' : '#2c2c2c',
              borderColor: '#e0e0e0',
              '&:hover': {
                backgroundColor: activeTab === 2 ? '#1a1a1a' : '#f5f5f5',
                borderColor: '#e0e0e0',
              },
            }}
          >
            <Badge badgeContent={incomingRequests.length} color="error">
              Requests
            </Badge>
          </Button>
        </Box>

        {/* Profile Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={4}>
            {/* Profile Details Section */}
            <Grid size={{ xs: 12, sm: 12 }}>
              {!isEditing ? (
                <Box>
                  <Grid
                    sx={{
                      textAlign: 'center',
                      flexDirection: 'column',
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <ProfileAvatar>{userInitial}</ProfileAvatar>
                    <Typography variant="body2" color="textSecondary">
                      {profile.email}
                    </Typography>
                  </Grid>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Bio
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 0 }}>
                        {profile.biography || 'No Bio provided.'}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Display Name
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {profile.displayName || 'Not set'}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Email
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {profile.email}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Location
                      </Typography>
                      <Typography variant="body2">{profile.location || 'Not set'}</Typography>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        WSDCID
                      </Typography>
                      <Typography variant="body2">{profile.wsdcId || 'Not set'}</Typography>
                      {profile.divisions && profile.divisions.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                            Divisions
                          </Typography>
                          {profile.divisions.map((div, index) => (
                            <Typography variant="body2" key={index}>
                              {div.division} - {div.role}
                            </Typography>
                          ))}
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <EditButton
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditToggle}
                    fullWidth
                  >
                    Edit Profile
                  </EditButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <StyledTextField
                    fullWidth
                    label="Bio"
                    value={editForm.biography || ''}
                    onChange={(e) => setEditForm({ ...editForm, biography: e.target.value })}
                    disabled={saving}
                    variant="outlined"
                    multiline
                    rows={4}
                  />

                  <StyledTextField
                    fullWidth
                    label="Display Name"
                    value={editForm.displayName || ''}
                    onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                    disabled={saving}
                    variant="outlined"
                  />

                  <StyledTextField
                    fullWidth
                    label="Profile Picture URL"
                    value={editForm.profilePicture || ''}
                    onChange={(e) => setEditForm({ ...editForm, profilePicture: e.target.value })}
                    disabled={saving}
                    variant="outlined"
                    placeholder="https://example.com/image.jpg"
                  />

                  <StyledTextField
                    fullWidth
                    label="WSDC ID"
                    value={editForm.wsdcId || ''}
                    onChange={(e) => setEditForm({ ...editForm, wsdcId: e.target.value })}
                    disabled={saving}
                    variant="outlined"
                    placeholder="Enter your WSDC ID"
                  />

                  <StyledTextField
                    fullWidth
                    label="Location (city, state, country)"
                    value={editForm.location || ''}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                    disabled={saving}
                    variant="outlined"
                    placeholder="Enter your location (city, state, country)"
                  />

                  {/* Add Divisions Section */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Divisions
                    </Typography>

                    {/* Display added divisions */}
                    {editForm.divisions && editForm.divisions.length > 0 && (
                      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {editForm.divisions.map((div, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 2,
                              py: 1,
                              bgcolor: '#f5f5f5',
                              borderRadius: 1,
                              fontSize: '0.875rem',
                            }}
                          >
                            <Typography variant="body2">
                              {div.division} -{' '}
                              {div.role.charAt(0).toUpperCase() + div.role.slice(1)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDivision(index)}
                              disabled={saving}
                              sx={{ color: '#e74c3c' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Division Select */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Select a Division</InputLabel>
                      <Select
                        value={newDivision}
                        onChange={(e) => setNewDivision(e.target.value)}
                        disabled={saving}
                        label="Select a Division"
                      >
                        {defaultDivisions.map((division) => (
                          <MenuItem key={division} value={division}>
                            {division}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Role Select with Add Button */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <FormControl fullWidth>
                        <InputLabel>Select a Role</InputLabel>
                        <Select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          disabled={saving}
                          label="Select a Role"
                        >
                          <MenuItem value="Leader">Leader</MenuItem>
                          <MenuItem value="Follower">Follower</MenuItem>
                          <MenuItem value="Both">Both</MenuItem>
                        </Select>
                      </FormControl>
                      <AddButton
                        variant="contained"
                        onClick={handleAddDivision}
                        disabled={!newDivision || !newRole || saving}
                        sx={{ minHeight: 56 }}
                      >
                        Add
                      </AddButton>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveProfile}
                      disabled={saving}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        flex: 1,
                      }}
                    >
                      {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleEditToggle}
                      disabled={saving}
                      sx={{ flex: 1 }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              )}
            </Grid>
          </Grid>
        )}
        {/* Events Tab Content */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {profile.events && profile.events.length > 0 ? (
              profile.events.map((eventAttendance) => (
                <Card key={eventAttendance.eventId} variant="outlined">
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {eventAttendance.eventName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                          {eventAttendance.eventDate
                            ? typeof eventAttendance.eventDate === 'string'
                              ? new Date(eventAttendance.eventDate).toLocaleDateString()
                              : eventAttendance.eventDate.toDate
                                ? eventAttendance.eventDate.toDate().toLocaleDateString()
                                : 'Date TBD'
                            : 'Date TBD'}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: eventAttendance.logistics?.isRegistered ? '#e8f5e9' : '#fff3e0',
                          color: eventAttendance.logistics?.isRegistered ? '#2e7d32' : '#e65100',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {eventAttendance.logistics?.isRegistered ? 'Registered' : 'Attending'}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Alert severity="info">
                No events yet.{' '}
                <Button size="small" onClick={() => navigate('/events')}>
                  Browse Events
                </Button>
              </Alert>
            )}
          </Box>
        )}

        {/* Requests Tab Content */}
        {activeTab === 2 && (
          <Box>
            {/* Sub-tabs for requests */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Button
                size="small"
                onClick={() => setRequestsSubTab(0)}
                sx={{
                  textTransform: 'none',
                  borderBottom: requestsSubTab === 0 ? 2 : 0,
                  borderColor: '#667eea',
                  borderRadius: 0,
                  color: requestsSubTab === 0 ? '#667eea' : 'text.secondary',
                }}
              >
                <Badge badgeContent={incomingRequests.length} color="error">
                  Incoming
                </Badge>
              </Button>
              <Button
                size="small"
                onClick={() => setRequestsSubTab(1)}
                sx={{
                  textTransform: 'none',
                  borderBottom: requestsSubTab === 1 ? 2 : 0,
                  borderColor: '#667eea',
                  borderRadius: 0,
                  color: requestsSubTab === 1 ? '#667eea' : 'text.secondary',
                }}
              >
                Outgoing ({outgoingRequests.length})
              </Button>
              <Button
                size="small"
                onClick={() => setRequestsSubTab(2)}
                sx={{
                  textTransform: 'none',
                  borderBottom: requestsSubTab === 2 ? 2 : 0,
                  borderColor: '#667eea',
                  borderRadius: 0,
                  color: requestsSubTab === 2 ? '#667eea' : 'text.secondary',
                }}
              >
                Matched ({matchedRequests.length})
              </Button>
            </Box>

            {requestsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Incoming Requests */}
                {requestsSubTab === 0 && (
                  <>
                    {incomingRequests.length === 0 ? (
                      <Alert severity="info">No pending partner requests.</Alert>
                    ) : (
                      <List>
                        {incomingRequests.map((request) => {
                          const isExpanded = expandedRequestIds.has(request.id);
                          const hasMessage = !!request.message;

                          return (
                            <Box key={request.id}>
                              <ListItem divider={!isExpanded}>
                                <ListItemAvatar>
                                  <Avatar>{request.requesterName[0]}</Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={1}>
                                      {request.requesterName}
                                      {hasMessage && (
                                        <IconButton
                                          size="small"
                                          onClick={() => toggleRequestExpansion(request.id)}
                                          sx={{ ml: 0.5, p: 0.25 }}
                                        >
                                          {isExpanded ? (
                                            <ExpandLessIcon fontSize="small" />
                                          ) : (
                                            <ExpandMoreIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="body2" component="span">
                                      {request.eventName} • {request.competitionName}
                                    </Typography>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    color="success"
                                    onClick={() => handleAcceptRequest(request.id)}
                                  >
                                    <CheckIcon />
                                  </IconButton>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeclineRequest(request.id)}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                              {hasMessage && (
                                <Collapse in={isExpanded}>
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 1.5,
                                      ml: 7,
                                      mr: 2,
                                      mb: 1,
                                      bgcolor: 'grey.50',
                                      borderRadius: 1,
                                      borderLeft: '3px solid',
                                      borderColor: 'primary.main',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontStyle: 'italic' }}
                                    >
                                      "{request.message}"
                                    </Typography>
                                  </Box>
                                  <Divider />
                                </Collapse>
                              )}
                            </Box>
                          );
                        })}
                      </List>
                    )}
                  </>
                )}

                {/* Outgoing Requests */}
                {requestsSubTab === 1 && (
                  <>
                    {outgoingRequests.length === 0 ? (
                      <Alert severity="info">
                        No pending outgoing requests.{' '}
                        <Button size="small" onClick={() => navigate('/discover')}>
                          Find Partners
                        </Button>
                      </Alert>
                    ) : (
                      <List>
                        {outgoingRequests.map((request) => (
                          <ListItem key={request.id} divider>
                            <ListItemAvatar>
                              <Avatar>{request.targetName[0]}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={request.targetName}
                              secondary={`${request.eventName} • ${request.competitionName}`}
                            />
                            <ListItemSecondaryAction>
                              <Chip label="Pending" size="small" color="warning" sx={{ mr: 1 }} />
                              <IconButton
                                color="error"
                                onClick={() => handleCancelRequest(request.id)}
                                title="Cancel Request"
                              >
                                <CloseIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}

                {/* Matched Requests */}
                {requestsSubTab === 2 && (
                  <>
                    {matchedRequests.length === 0 ? (
                      <Alert severity="info">No matched partners yet. Keep searching!</Alert>
                    ) : (
                      <List>
                        {matchedRequests.map((request) => {
                          const isRequester = request.requesterId === profile?.uid;
                          const partnerName = isRequester
                            ? request.targetName
                            : request.requesterName;
                          const partnerId = isRequester ? request.targetId : request.requesterId;

                          return (
                            <ListItem key={request.id} divider>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'success.main' }}>{partnerName[0]}</Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    {partnerName}
                                    <VerifiedIcon color="success" fontSize="small" />
                                  </Box>
                                }
                                secondary={`${request.eventName} • ${request.competitionName}`}
                              />
                              <ListItemSecondaryAction>
                                <Button
                                  size="small"
                                  onClick={() => navigate(`/users/${partnerId}`)}
                                >
                                  View Profile
                                </Button>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </>
                )}
              </>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/discover')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Find Partners
              </Button>
            </Box>
          </Box>
        )}
      </ProfileCard>

      <LogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
      />
    </ProfileContainer>
  );
};

// Logout Confirmation Dialog
const LogoutDialog = ({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Confirm Logout</DialogTitle>
    <DialogContent>
      <Typography>Are you sure you want to log out?</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Logout
      </Button>
    </DialogActions>
  </Dialog>
);
